import { useState,useRef, useEffect } from 'react';
import { Link, useLocation,useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { authAPI } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Sun, Moon, Copy, Hash, Play } from 'lucide-react';
import { MetaTradifyLogo } from '@/components/MetaTradifyLogo';
import { toast } from 'sonner';

export default function RegisterPage() {
  const turnstileRef = useRef(null);
  const location = useLocation();
  const googleData = location.state?.googleData;
  const { register, setAuth } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
   const [form, setForm] = useState({ name: googleData?.name || "",
  email: googleData?.email || "",
  phone: "",
  password: "",
  confirmPassword: "" });
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [newUserId, setNewUserId] = useState('');
  const [demoLoading, setDemoLoading] = useState(false);
const [captchaToken, setCaptchaToken] = useState(null);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.password) {
      toast.error('Please fill in all fields'); return;
    }
    if (form.password !== form.confirmPassword) {
      toast.error('Passwords do not match'); return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters'); return;
    }
   if (!/^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{6,}$/.test(form.password.trim())) {
  toast.error('Password must have letter, number & special character');
  return;
}
    setLoading(true);
    try {
      const data = await register({ name: form.name, email: form.email, phone: form.phone, password: form.password });
      if (data?.user?.user_id) {
        setNewUserId(data.user.user_id);
        setShowSuccess(true);
      } else {
        toast.success('Account created! Welcome to metaTradify');
        navigate('/dashboard');
        if (
  window.location.pathname.startsWith(
    '/mobile-auth'
  )
) {

  window.location.href =
    `metatradify://auth?token=${data.token}`;

  return;
}
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoAccount = async () => {
    setDemoLoading(true);
    try {
      const { data } = await authAPI.demoRegister();
      localStorage.setItem('token', data.token);
      setAuth(data.user, data.token);
      toast.success(`Demo account created! ID: ${data.user.user_id} — expires in 24 hours`);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create demo account');
    } finally { setDemoLoading(false); }
  };

  const update = (field, value) => setForm({ ...form, [field]: value });
useEffect(() => {
  if (!window.turnstile || !turnstileRef.current) return;

  // 🔥 prevent double render
  if (turnstileRef.current.hasChildNodes()) return;

  window.turnstile.render(turnstileRef.current, {
    sitekey: "0x4AAAAAAC2ixNnM6mujfOy5",
    callback: (token) => setCaptchaToken(token),
    theme: "light",
    size: "flexible"
  });

}, []);;
  return (
    <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center px-4 py-8" data-testid="register-page">
      <button onClick={toggleTheme} data-testid="register-theme-toggle" className="absolute top-4 right-4 p-2 rounded-lg bg-[var(--hover-bg)] border border-[var(--border-subtle)] transition-all z-10">
        {theme === 'dark' ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-green-500" />}
      </button>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-green-500/5 rounded-full blur-[100px]" />
      </div>
      <div className="w-full max-w-[400px] relative">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <MetaTradifyLogo size={32} />
            <span className="text-lg font-bold font-['Outfit'] text-[var(--text-primary)]">metaTradify</span>
          </Link>
          <h1 className="text-2xl font-bold font-['Outfit'] tracking-tight text-[var(--text-primary)]">Create your account</h1>
          {/* <p className="text-sm text-[var(--text-secondary)] mt-1">Start trading with $10,000 balance</p> */}
        </div>

        {/* Demo Account Quick Access */}
        <button
          onClick={handleDemoAccount}
          disabled={!captchaToken || demoLoading}
          data-testid="register-demo-btn"
          className="w-full h-11 mb-4 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded text-sm font-medium text-amber-400 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <Play className="w-4 h-4" />
          {demoLoading ? 'Creating demo...' : 'Skip registration — Try Demo Account'}
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 h-px bg-[var(--border-subtle)]" />
          <span className="text-xs text-[var(--text-tertiary)]">or create a full account</span>
          <div className="flex-1 h-px bg-[var(--border-subtle)]" />
        </div>

        <form  onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm text-[var(--text-secondary)]">Full Name</Label>
            <Input id="name" data-testid="register-name-input" placeholder="John Doe" value={form.name}readOnly={!!googleData} onChange={(e) => update('name', e.target.value)} className="bg-[var(--bg-surface)] border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:border-green-500 focus:ring-green-500/20 h-11" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm text-[var(--text-secondary)]">Email</Label>
            <Input id="email" type="email" data-testid="register-email-input" placeholder="you@example.com" value={form.email}readOnly={!!googleData} onChange={(e) => update('email', e.target.value)} className="bg-[var(--bg-surface)] border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:border-green-500 focus:ring-green-500/20 h-11" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-sm text-[var(--text-secondary)]">Phone</Label>
            <Input id="phone" type="tel" data-testid="register-phone-input" placeholder="+1 (555) 000-0000" value={form.phone} onChange={(e) => update('phone', e.target.value)} className="bg-[var(--bg-surface)] border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:border-green-500 focus:ring-green-500/20 h-11" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm text-[var(--text-secondary)]">Password</Label>
            <div className="relative">
              <Input id="password" type={showPass ? 'text' : 'password'} data-testid="register-password-input" placeholder="Min 6 characters" value={form.password} onChange={(e) => update('password', e.target.value)} className="bg-[var(--bg-surface)] border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:border-green-500 focus:ring-green-500/20 h-11 pr-10" />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] hover:text-[var(--text-secondary)]">
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-sm text-[var(--text-secondary)]">Confirm Password</Label>
            <Input id="confirmPassword" type="password" data-testid="register-confirm-input" placeholder="Repeat password" value={form.confirmPassword} onChange={(e) => update('confirmPassword', e.target.value)} className="bg-[var(--bg-surface)] border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:border-green-500 focus:ring-green-500/20 h-11" />
          </div>
          <div ref={turnstileRef} className="w-full"></div>
          <button type="submit" disabled={!captchaToken || loading} data-testid="register-submit-btn" className="w-full h-11 bg-green-500 hover:bg-green-600 text-white rounded text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <p className="text-center text-sm text-[var(--text-secondary)] mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-green-400 hover:text-green-300 transition-colors" data-testid="register-login-link">Sign in</Link>
        </p>

        {/* User ID Success Modal */}
        {showSuccess && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4" data-testid="registration-success-modal">
            <div className="bg-[var(--bg-surface)] border border-green-500/30 rounded-lg p-6 max-w-sm w-full text-center space-y-4">
              <div className="w-14 h-14 mx-auto rounded-full bg-green-500/15 flex items-center justify-center">
                <Hash className="w-7 h-7 text-green-400" />
              </div>
              <h2 className="text-lg font-bold text-[var(--text-primary)] font-['Outfit']">Account Created!</h2>
              <p className="text-sm text-[var(--text-secondary)]">Your unique User ID is:</p>
              <div className="flex items-center justify-center gap-2 p-3 bg-[var(--bg-primary)] rounded border border-green-500/20">
                <span className="text-3xl font-mono font-bold text-green-400 tracking-wider" data-testid="new-user-id">{newUserId}</span>
                <button onClick={() => { navigator.clipboard.writeText(newUserId); toast.success('Copied!'); }} className="p-1.5 hover:bg-[var(--hover-bg)] rounded" data-testid="copy-new-user-id">
                  <Copy className="w-4 h-4 text-[var(--text-secondary)]" />
                </button>
              </div>
              <p className="text-xs text-[var(--text-tertiary)]">Save this ID — you can use it to log in instead of email.</p>
              <button onClick={() => navigate('/dashboard')} data-testid="goto-dashboard-btn"
                className="w-full py-2.5 bg-green-500 hover:bg-green-600 text-white rounded text-sm font-medium transition-all">
                Go to Dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
